import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreateZoneRequest,
  CreateZoneRequestSchema,
  UpdateZoneRequestSchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import type { ZoneCapacityHint } from '../../lib/zoneCapacityHint';

// ── Types ──────────────────────────────────────────────────────────────────

interface ZoneFormProps {
  mode: 'create' | 'edit';
  onSubmit: (values: CreateZoneRequest) => Promise<void>;
  defaultValues?: Partial<CreateZoneRequest>;
  submitLabel?: string;
  onCancel?: () => void;
  /**
   * Live preview of remaining zone capacity for this location — see
   * computeZoneCapacityHint. Rendered next to the capacity field so the
   * admin sees how much is left to distribute while typing. Omitted or
   * `{ visible: false }` renders nothing (location has no defined capacity).
   */
  capacityHint?: ZoneCapacityHint;
}

// ── Component ──────────────────────────────────────────────────────────────

export function ZoneForm({
  mode,
  onSubmit,
  defaultValues,
  submitLabel,
  onCancel,
  capacityHint,
}: ZoneFormProps) {
  const { t } = useTranslation('zones');
  const schema = mode === 'create' ? CreateZoneRequestSchema : UpdateZoneRequestSchema;
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateZoneRequest>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      capacity: null,
      ...defaultValues,
    },
  });

  async function handleFormSubmit(values: CreateZoneRequest) {
    setServerError(null);
    try {
      await onSubmit(values);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('errors.generic') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('form.name.label')} htmlFor="name" error={errors.name?.message}>
          <Input
            id="name"
            placeholder={t('form.name.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
          />
        </Field>

        <Field label={t('form.capacity.label')} htmlFor="capacity" error={errors.capacity?.message}>
          <Input
            id="capacity"
            type="number"
            min={1}
            placeholder={t('form.capacity.placeholder')}
            disabled={isSubmitting}
            {...register('capacity', {
              setValueAs: (v: string) => (v === '' ? null : Number(v)),
            })}
          />
          <p className="mt-1 text-[12px] text-jq-graphite">{t('form.capacity.hint')}</p>
          <CapacityHintNote hint={capacityHint} />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : (submitLabel ?? t('common:actions.save'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

// Reuses the same `capacitySummary.*` copy as ZonesIndexPage's list-level
// summary (see zones.json) — same wording, just interpolated with the
// exclude-self numbers computed by computeZoneCapacityHint.
function CapacityHintNote({ hint }: { hint?: ZoneCapacityHint }) {
  const { t } = useTranslation('zones');
  if (!hint || !hint.visible) return null;

  return (
    <p className="mt-1 text-[12px] text-jq-graphite">
      {hint.status === 'over'
        ? t('capacitySummary.over', {
            assigned: hint.assigned,
            total: hint.total,
            over: hint.overBy,
          })
        : t('capacitySummary.remaining', {
            assigned: hint.assigned,
            total: hint.total,
            remaining: hint.remaining,
          })}
    </p>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
